/* Mantleford storefront analytics. No Google request until Shopify permits analytics. */
(() => {
  'use strict';
  const w = window, d = document;
  if (w.MantlefordGA4) return;
  let config, started = false, loading = false, loaded = false, configured = false, pageSent = false, moduleStarted = false, failed = false;
  const events = new Set(['outfit_view', 'build_look_view', 'compare_open', 'piece_change', 'affiliate_click', 'external_click']);
  const fields = new Set(['outfit_handle', 'aesthetic', 'catalog_item_id', 'piece_slot', 'retailer', 'cta_location', 'relationship']);
  const adsDenied = {ad_storage:'denied', ad_user_data:'denied', ad_personalization:'denied'};
  const mark = state => { d.documentElement.dataset.mfAnalytics = state; };
  function excluded() {
    try {
      const url = new URL(w.location.href);
      if (url.searchParams.get('mf_analytics') === 'off') w.sessionStorage.setItem('mf_analytics_excluded', '1');
      if (w.sessionStorage.getItem('mf_analytics_excluded') === '1') return true;
      return !['mantleford.com','www.mantleford.com'].includes(url.hostname) || url.protocol !== 'https:' ||
        w.self !== w.top || (w.Shopify?.theme?.role && w.Shopify.theme.role !== 'main') ||
        config?.preview === true || w.Shopify?.designMode === true || w.Shopify?.visualPreviewMode === true ||
        d.documentElement.hasAttribute('data-mf-analytics-disabled') || url.searchParams.get('analytics_test') === '1' ||
        ['preview_theme_id','preview_script_id','theme_editor','preview_token','_preview','pb'].some(key => url.searchParams.has(key)) ||
        !/^\/(?:pages\/[a-z0-9-]+\/?)?$/.test(url.pathname);
    } catch (_) { return true; }
  }
  function permission() {
    if (!config?.enabled || config.privacyPreferencesReady !== true || excluded()) return false;
    try { return w.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch (_) { return false; }
  }
  function context() {
    const url = new URL(w.location.href);
    const page = new URL(url.origin + url.pathname);
    // Public campaign labels only. Never transmit arbitrary queries, fragment or contact values.
    for (const key of ['utm_source','utm_medium','utm_campaign','utm_content']) {
      const value = url.searchParams.get(key);
      if (value && /^[a-zA-Z0-9_-]{1,80}$/.test(value)) page.searchParams.set(key,value);
    }
    let referrer = '';
    try {
      const ref = new URL(d.referrer);
      if (ref.protocol === 'https:' || ref.protocol === 'http:') referrer = ref.origin + '/';
    } catch (_) {}
    return {page_location:page.href, page_referrer:referrer, page_title:d.title.slice(0,150)};
  }
  function gtag() { w.dataLayer.push(arguments); }
  function publish(name, detail) {
    if (!events.has(name) || !loaded || !configured || failed || !permission()) return false;
    const clean = {};
    for (const [key,value] of Object.entries(detail || {})) {
      const format = key === 'retailer' ? /^[a-zA-Z0-9 _&./:'\u2019-]+$/ : /^[a-zA-Z0-9 _&./:-]+$/;
      if (fields.has(key) && typeof value === 'string' && value.length <= 100 && format.test(value)) {
        const normalized = key === 'retailer' ? value.trim().toLowerCase() : value;
        if (normalized) clean[key] = normalized;
      }
    }
    // Do not use a global default destination or infer a purchase from a retailer exit.
    gtag('event',name,{...clean,...context(),send_to:config.measurementId});
    return true;
  }
  function allowLoadedTag() {
    if (!loaded || failed || !permission()) return;
    w['ga-disable-' + config.measurementId] = false;
    gtag('consent','update',{...adsDenied,analytics_storage:'granted'});
    if (!configured) {
      gtag('js',new Date());
      gtag('set','ads_data_redaction',true);
      gtag('set','url_passthrough',false);
      gtag('config',config.measurementId,{
        send_page_view:false,
        allow_google_signals:false,
        allow_ad_personalization_signals:false,
        ...context()
      });
      configured = true;
    }
    if (!pageSent && d.visibilityState === 'visible') {
      pageSent = true;
      gtag('event','page_view',{...context(),send_to:config.measurementId});
    }
    if (!moduleStarted && w.MantlefordEngagement?.start) {
      moduleStarted = true;
      w.MantlefordEngagement.start({publish,analyticsAllowed:() => loaded && !failed && permission()});
    }
    mark('ready');
  }
  function sync() {
    if (!started || !config) return;
    if (!permission()) {
      w['ga-disable-' + config.measurementId] = true;
      if (loaded) gtag('consent','update',{...adsDenied,analytics_storage:'denied'});
      mark(excluded() ? 'excluded' : 'consent-blocked');
      return;
    }
    if (failed) { mark('unavailable'); return; }
    if (loaded) { allowLoadedTag(); return; }
    if (loading) return;
    // Refuse to install alongside another top-frame Google setup. Resolve its ownership first.
    if (typeof w.gtag === 'function' || d.querySelector('script[src*="googletagmanager.com/gtag/js"],script[src*="googletagmanager.com/gtm.js"]')) {
      failed = true;
      mark('existing-tag-conflict');
      return;
    }
    loading = true;
    w['ga-disable-' + config.measurementId] = true;
    w.dataLayer = w.dataLayer || [];
    if (!Array.isArray(w.dataLayer)) { failed = true; mark('existing-tag-conflict'); return; }
    w.gtag = gtag;
    gtag('consent','default',{...adsDenied,analytics_storage:'denied'});
    const script = d.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(config.measurementId);
    script.dataset.mfGoogleTag = 'true';
    script.onload = () => { loaded = true; loading = false; sync(); };
    script.onerror = () => { failed = true; loading = false; w['ga-disable-' + config.measurementId] = true; mark('unavailable'); };
    d.head.appendChild(script);
    mark('loading');
  }
  function start() {
    if (started) return;
    const node = d.getElementById('mf-ga4-config');
    try { config = JSON.parse(node?.textContent || '{}'); } catch (_) { mark('invalid-config'); return; }
    if (!config.enabled) { mark('disabled'); return; }
    if (!/^G-[A-Z0-9]{6,20}$/.test(config.measurementId || '') || config.privacyPreferencesReady !== true) { mark('invalid-config'); return; }
    started = true;
    d.addEventListener('visitorConsentCollected',sync);
    d.addEventListener('mantleford:analytics-preferences-saved',sync);
    d.addEventListener('visibilitychange',sync);
    sync();
    if (!excluded() && !w.Shopify?.customerPrivacy && typeof w.Shopify?.loadFeatures === 'function') {
      try { w.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}],error => { if (!error) sync(); }); }
      catch (_) { mark('consent-unavailable'); }
    }
  }
  w.MantlefordGA4 = Object.freeze({start});
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded',start,{once:true});
  else start();
})();
